import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { billingApi, subscriptionsApi, formatApiError } from '../lib/api';
import { subscribeQuotaReached } from '../lib/quotaGate';
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { AlertOctagon, Loader2, Zap, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const BILLING_ROLES = ['admin', 'manager', 'billing_admin'];

export default function QuotaReachedDialog() {
    const { t } = useTranslation();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [purchasing, setPurchasing] = useState(false);
    const [currentPlan, setCurrentPlan] = useState(null);

    useEffect(() => {
        const unsub = subscribeQuotaReached(() => setOpen(true));
        return unsub;
    }, []);

    // Fetch the plan lazily when the dialog opens so we know whether to hide
    // the "buy pack 50" CTA (Free plan is not eligible — backend also blocks
    // the call with HTTP 403). Errors fail-open to keep the modal usable.
    useEffect(() => {
        if (!open || !user) return;
        let cancelled = false;
        subscriptionsApi.getCurrent()
            .then((res) => {
                if (!cancelled) setCurrentPlan(res.data?.plan || null);
            })
            .catch(() => {
                if (!cancelled) setCurrentPlan(null);
            });
        return () => { cancelled = true; };
    }, [open, user]);

    const canPay = !!user && BILLING_ROLES.includes(user.role);
    const isFreePlan = currentPlan === 'free';

    const goToOverage = () => {
        setOpen(false);
        navigate('/dashboard/settings/subscription#overage');
    };

    const goToPlans = () => {
        setOpen(false);
        navigate('/dashboard/settings/subscription');
    };

    const handleBuyPack50 = async () => {
        if (purchasing) return;
        setPurchasing(true);
        try {
            const res = await billingApi.createOverageCheckout('pack_50', window.location.origin);
            const url = res.data?.url;
            if (url) {
                window.location.href = url;
                return;
            }
            throw new Error('Stripe URL missing');
        } catch (err) {
            toast.error(formatApiError(err));
            setPurchasing(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent
                data-testid="quota-reached-dialog"
                className="w-[calc(100vw-2rem)] sm:max-w-lg"
            >
                <DialogHeader>
                    <div className="mx-auto mb-2 w-12 h-12 rounded-full bg-[#FEF3C7] text-[#B45309] flex items-center justify-center">
                        <AlertOctagon className="w-6 h-6" />
                    </div>
                    <DialogTitle className="text-center text-[#0A0A0A] font-['Outfit'] text-xl">
                        {t('quota_reached.title')}
                    </DialogTitle>
                    <DialogDescription className="text-center text-[#4B5563] break-words">
                        {canPay
                            ? (isFreePlan
                                ? t('quota_reached.desc_free_billing')
                                : t('quota_reached.desc_billing'))
                            : t('quota_reached.desc_recruiter')}
                    </DialogDescription>
                </DialogHeader>

                {canPay ? (
                    <DialogFooter className="flex flex-col sm:flex-row sm:justify-center gap-2 sm:gap-3 pt-2">
                        {!isFreePlan && (
                            <Button
                                type="button"
                                onClick={handleBuyPack50}
                                disabled={purchasing}
                                data-testid="quota-reached-buy-pack50"
                                className="w-full sm:w-auto bg-[#002FA7] hover:bg-[#002480] text-white whitespace-normal"
                            >
                                {purchasing ? (
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin shrink-0" />
                                ) : (
                                    <Zap className="w-4 h-4 mr-2 shrink-0" />
                                )}
                                <span>{t('quota_reached.buy_pack_50')}</span>
                            </Button>
                        )}
                        <Button
                            type="button"
                            variant={isFreePlan ? 'default' : 'outline'}
                            onClick={goToPlans}
                            data-testid="quota-reached-see-plans"
                            className={
                                isFreePlan
                                    ? 'w-full sm:w-auto bg-[#002FA7] hover:bg-[#002480] text-white whitespace-normal'
                                    : 'w-full sm:w-auto whitespace-normal'
                            }
                        >
                            <span>{t('quota_reached.see_plans')}</span>
                            <ArrowRight className="w-4 h-4 ml-2 shrink-0" />
                        </Button>
                    </DialogFooter>
                ) : (
                    <DialogFooter className="flex flex-col sm:flex-row sm:justify-center pt-2">
                        <Button
                            type="button"
                            onClick={goToOverage}
                            data-testid="quota-reached-see-options"
                            className="w-full sm:w-auto bg-[#002FA7] hover:bg-[#002480] text-white whitespace-normal"
                        >
                            <span>{t('quota_reached.see_options')}</span>
                            <ArrowRight className="w-4 h-4 ml-2 shrink-0" />
                        </Button>
                    </DialogFooter>
                )}
            </DialogContent>
        </Dialog>
    );
}
